import { useTranslation } from "react-i18next";
import { Navigate, Outlet, useLocation } from "@tanstack/react-router";

import { useAuth } from "../lib/auth";
import { ElevationProvider } from "../features/access/elevation";
import { landingFor, systemMayOpen } from "../features/access/routing";
import { AppShell } from "./app-shell";

/**
 * The gate in front of every page that needs a session ([F-029]: the system
 * knows the person before it shows anything that requires a login).
 *
 *   loading        -> the loading line
 *   anonymous      -> the login, remembering where the person was going
 *                     (not after a logout - [AC-051], the next person)
 *   system account -> the administration only ([F-031]); content pages send
 *                     it there instead of showing screens the server refuses
 *   otherwise      -> the shell, with the step-up dialog available to every
 *                     screen inside it ([F-033])
 */
export function AppLayout(): JSX.Element {
  const { t } = useTranslation();
  const { status, isSystem, reason } = useAuth();
  const location = useLocation();

  if (status === "loading") {
    return (
      <main className="flex min-h-screen items-center justify-center bg-secondary p-6">
        <p data-testid="app_loading" className="text-sm text-muted-foreground">
          {t("app.loading")}
        </p>
      </main>
    );
  }

  if (status === "anonymous") {
    // Back to the interrupted page after the login - unless the person logged
    // out: the next one at this device starts on their own first screen.
    const here = location.href;
    const back = reason !== "logout" && here !== "/";
    return <Navigate to="/anmelden" search={back ? { weiter: here } : {}} replace />;
  }

  if (isSystem && !systemMayOpen(location.pathname)) {
    return <Navigate to={landingFor(true)} replace />;
  }

  return (
    <ElevationProvider>
      <AppShell>
        <Outlet />
      </AppShell>
    </ElevationProvider>
  );
}
